/**
 * Territory. The same interactive city as Home, with the map as the working surface.
 *
 * **THIS SCREEN NOW READS THE REAL MAP.** `GET /api/territory` returns all forty blocks with the
 * holder, the tier income, the live Control value and the fortification tier, seeded at Era open
 * from `content/blocks.json`. Every name on it is authored: CONTENT-BIBLE 2.1 and 2.2 name 24
 * landmark blocks and 2.3 names the other sixteen, and `tools/content/validate.mjs` section 8
 * refuses any of the forty typed into this file as a literal.
 *
 * What it used to render, kept here because the deletion is the fix rather than a correction:
 *
 *   - **Forty invented block names**, including "Rossi Bros. Garage", built on a surname
 *     CONTENT-BIBLE:97 bans by name. `docs/DATA-MODEL.md` then cited the mock as a source.
 *   - **An Operations panel offering "Launch Raid" and "Claim" as instant buttons**, on a system
 *     where every contest resolves in one of four fixed weekly windows and the DEFENDER picks
 *     which one. An instant raid button is not an unbuilt feature, it is a different game.
 *   - **A "Defense" stat reading the district's HEAT**, on a screen with a real Defense column.
 *   - **Independent printed against all 24 rows**, because nothing held any ground.
 *
 * What is still not built says so and does not draw a fabricated version of itself. Raiding,
 * fortifying, declaring and Operations are stages B to D; the windows below are real, because
 * they are a pure function of the clock and need no server at all.
 */

import { useEffect, useState } from 'react'
import type { Block } from '@ashport/shared'
import {
  TERRITORY_CLIFF_BLOCKS,
  TERRITORY_PEAK_BLOCKS,
  upkeepDaily,
} from '@ashport/shared/formulas'
import { A } from '../art'
import { Icon } from '../ui/Icon'
import { Chip, Col, Panel, PanelHead, Rail, Row, Stack, Stat } from '../ui/Kit'
import { sz, useViewport } from '../ui/viewport'
import { CityMap } from '../home/CityMap'
import { districts, controlLabel, controlTint, exact, moneyD, useGame } from '../game/state'
import { useSession } from '../game/session'

/**
 * Upkeep is a function of HOW MANY blocks you hold and of nothing else.
 *
 * This screen used to carry its own curve, `1 + (held - 1)^1.35 / 22`, which was the third upkeep
 * formula in the repository and agreed with neither of the others. There is one curve, it lives in
 * `shared/formulas.ts`, and the server charges against it. A cost may be computed here because a
 * cost is deterministic and printed on the button before the tap; an OUTCOME never may.
 */
const upkeepFor = (held: number) => upkeepDaily(held) / 100

const TIER_LABEL = ['', 'Starter', 'Mid', 'Prime'] as const

function Empty({ line, note }: { line: string; note: string }) {
  return (
    <div style={{ padding: '22px 18px', textAlign: 'center' }}>
      <Icon n="pin" s={22} style={{ opacity: 0.4 }} />
      <div
        className="t-head"
        style={{ fontSize: 12, color: 'var(--gold)', letterSpacing: '.12em', marginTop: 8 }}
      >
        {line}
      </div>
      <p style={{ margin: '5px 0 0', fontSize: 11.5, color: 'var(--ink-mid)' }}>{note}</p>
    </div>
  )
}

function Ledger() {
  const mine = useSession((s) => s.territory?.mine ?? null)
  const busy = useSession((s) => s.busy)
  const collect = useSession((s) => s.collectTerritory)
  const buyBooks = useSession((s) => s.buyBooks)
  const held = mine?.blocksHeld ?? 0
  // What ONE MORE block would cost, which is the number a player is deciding against.
  const marginal = upkeepFor(held + 1) - upkeepFor(held)

  return (
    <Panel tone="gold" style={{ flex: 'none' }}>
      <PanelHead title="Daily Ledger" icon="ledger" accent="var(--gold)" />
      <div style={{ padding: '10px 12px 12px' }}>
        <Row style={{ justifyContent: 'space-between' }}>
          <Stat label="Blocks Held" value={held} />
          <Stat
            label="Next Block Costs"
            value={moneyD(marginal)}
            c="var(--heat)"
            align="right"
            sub="per day, on top"
          />
        </Row>
        {mine !== null && held > 0 && (
          <Row style={{ justifyContent: 'space-between', marginTop: 10 }}>
            <Stat label="Income" value={exact(mine.incomeDaily)} sub="a day, after the skim" />
            <Stat
              label="Net"
              value={exact(mine.netDaily)}
              c={mine.netDaily < 0 ? 'var(--heat)' : 'var(--money)'}
              align="right"
              sub={mine.skimBp > 0 ? `Books skim ${(mine.skimBp / 100).toFixed(0)}%` : 'no skim'}
            />
          </Row>
        )}
        {/*
          * THE TILL AND THE BILL, which is the pair this screen printed a forecast for and could
          * not show for a whole phase. Income accrues hourly from an anchor per block and stops at
          * sixty hours; upkeep accrues from an anchor on the family and is settled by this button.
          */}
        {mine !== null && held > 0 && (
          <Col gap={7} style={{ marginTop: 10 }}>
            <Row style={{ justifyContent: 'space-between' }}>
              <Stat
                label="In the till"
                value={exact(mine.till)}
                c="var(--money)"
                sub={mine.tillCapped ? 'full, and no longer earning' : `${mine.tillHours}h banked`}
              />
              <Stat
                label="Owed"
                value={exact(mine.upkeepOwed)}
                c={mine.upkeepOwed > 0 ? 'var(--heat)' : 'var(--ink-mid)'}
                align="right"
                sub={mine.upkeepMissed > 0 ? 'a reckoning missed' : 'settled on collection'}
              />
            </Row>
            <button
              className="btn"
              disabled={busy || mine.tillHours < 1}
              onClick={() => void collect()}
            >
              {mine.tillHours < 1 ? 'Nothing to take yet' : `Collect ${exact(mine.till)}`}
            </button>
            {mine.booksNext !== null && (
              <button
                className="btn"
                disabled={busy}
                onClick={() => void buyBooks(mine.booksNext!.cost)}
              >
                Books to {mine.booksNext.level} for {exact(mine.booksNext.cost)}
              </button>
            )}
          </Col>
        )}
        <p style={{ margin: '10px 0 0', fontSize: 11, color: 'var(--ink-dim)', lineHeight: 1.5 }}>
          Upkeep climbs faster than income. It pays best at {TERRITORY_PEAK_BLOCKS} blocks, and
          past {TERRITORY_CLIFF_BLOCKS} the map costs more than it pays. Holding ground past that
          is a decision to deny it to somebody else.
        </p>
      </div>
    </Panel>
  )
}

/**
 * The next four windows and the countdown, which are real and computed here.
 *
 * The server sends them because it is the authority on the clock, and it derives them from the
 * same pure function this screen could have called: `shared/war-window.ts` takes an instant and
 * returns the windows, with no Durable Object read, no schedule fetch and no cron. That is
 * `shared/beat.ts`'s architecture a second time, and it is why GAME-DESIGN 8.6's nomination table
 * could be deleted rather than built.
 */
function Windows() {
  const t = useSession((s) => s.territory)
  const { now } = useGame()
  const windows = t?.windows ?? []
  const open = t?.openWindow ?? null

  return (
    <Panel style={{ flex: 'none' }}>
      <PanelHead
        title="War Windows"
        icon="target"
        sub={open !== null ? 'A window is open' : 'Four a week'}
      />
      <div style={{ padding: '9px 12px 12px' }}>
        {windows.length === 0 ? (
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-dim)' }}>Reading the clock.</p>
        ) : (
          <Col gap={5}>
            {windows.map((w) => {
              const away = Math.max(0, w.startsAt - now)
              const hours = Math.floor(away / 3_600_000)
              const days = Math.floor(hours / 24)
              return (
                <Row key={w.index} style={{ justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 11.5, color: 'var(--ink-hi)' }}>{w.label}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--ink-dim)' }}>
                    {w.index === open
                      ? 'open now'
                      : days > 0
                        ? `in ${days}d ${hours % 24}h`
                        : `in ${hours}h`}
                  </span>
                </Row>
              )
            })}
          </Col>
        )}
        <p style={{ margin: '10px 0 0', fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.55 }}>
          Ground never changes hands on a button. The attacker declares and strikes one of the
          four; the defender picks which of the remaining three it resolves in.
        </p>
        <p style={{ margin: '9px 0 0', fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.55 }}>
          Presence is capped at five a side per block, so a bigger family buys breadth and never
          depth. Twice an Era, on days 28 and 56, every block reverts and the whole city fights an
          open map in one window.
        </p>
      </div>
    </Panel>
  )
}

/** Why this crew cannot take ground yet, in the server's words. Never computed here. */
function Gate() {
  const gate = useSession((s) => s.territory?.gate ?? null)
  if (gate === null || gate.open) return null
  return (
    <Panel style={{ flex: 'none' }}>
      <PanelHead title="Not yet" icon="lock" sub={`${gate.have} of ${gate.need}`} />
      <p
        style={{
          margin: 0,
          padding: '9px 12px 12px',
          fontSize: 11.5,
          color: 'var(--ink-mid)',
          lineHeight: 1.55,
        }}
      >
        {gate.reason}
      </p>
    </Panel>
  )
}

/**
 * WHAT CAN BE DONE TO ONE BLOCK, and what it costs, printed before the tap.
 *
 * The client computes a COST and never an outcome. Every number on these buttons arrives on the
 * payload: the Nerve a raid costs including the Beat multiplier, what the doors absorb, the range
 * that leaves, and the chance the door holds. 1j.12's rule is to publish everything a userscript
 * would compute, and this panel is that rule with a border round it.
 *
 * The buttons that are shut say WHY. A dead control with no reason on it is the thing this screen
 * was rebuilt to stop doing.
 */
function BlockActions({ b }: { b: Block | null }) {
  const t = useSession((s) => s.territory)
  const busy = useSession((s) => s.busy)
  const last = useSession((s) => s.lastTerritory)
  const refusal = useSession((s) => s.refusal)
  const raid = useSession((s) => s.raidBlock)
  const fortify = useSession((s) => s.fortifyBlock)
  const declare = useSession((s) => s.declareWar)
  const cede = useSession((s) => s.cedeBlock)
  const answer = useSession((s) => s.answerWar)
  const [slot, setSlot] = useState(0)

  if (b === null || t === null) return null
  const open = t.openWindow !== null
  const mine = b.owner === 'you'
  /*
   * GROUND IS HELD BY CREWS, AND AN UNAFFILIATED PLAYER WAS NOT TOLD SO UNTIL THEY TAPPED.
   *
   * `familyOf` in `server/src/routes/territory.ts` refuses every verb on this panel with FORBIDDEN
   * when the caller has no family, which is correct and is the first thing a new account hits: all
   * four buttons were live, all four answered 403, and the panel header three lines above this one
   * says "the buttons that are shut say WHY". `mine` on the payload is null for exactly this
   * player, so the state was already on the wire and nothing read it.
   */
  const inCrew = t.mine !== null
  const war = t.wars.find((w) => w.blockId === b.blockId) ?? null
  const toAnswer = war !== null && war.answerable.length > 0 ? war : null

  return (
    <Panel style={{ flex: 'none' }}>
      <PanelHead
        title={b.name}
        icon="target"
        sub={`${b.fortTierName}, Control ${b.control}`}
        right={<Chip c={controlTint[b.owner]}>{b.ownerFamilyName ?? 'Independent'}</Chip>}
      />
      <Col gap={7} style={{ padding: '9px 12px 12px' }}>
        {b.standingEffect !== null && (
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--gold)', lineHeight: 1.5 }}>
            {b.standingEffect.name}. {b.standingEffect.effect}.
          </p>
        )}
        {b.raid !== null && !mine && (
          <p style={{ margin: 0, fontSize: 11, color: 'var(--ink-dim)', lineHeight: 1.5 }}>
            {b.raid.nerve} Nerve. Knocks {b.raid.minControl} to {b.raid.maxControl} off after the
            doors take {b.raid.absorb}.
            {b.raid.absorbedBp > 0
              ? ` The door holds ${(b.raid.absorbedBp / 100).toFixed(1)}% of the time, and a hinge goes with it.`
              : ''}
          </p>
        )}
        {!inCrew && (
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--gold)', lineHeight: 1.5 }}>
            Ground is held by crews. You are not in one, so none of this is yours to do yet.
          </p>
        )}
        <button className="btn" disabled={busy || !inCrew} onClick={() => void fortify(b.blockId)}>
          {mine ? 'Work the doors up' : open ? 'Pull a tier off the doors' : 'Doors, in a window'}
        </button>
        {!mine && (
          <button
            className="btn"
            disabled={busy || !open || !inCrew}
            onClick={() => void raid(b.blockId)}
          >
            {open ? `Raid for ${b.raid?.nerve ?? 0} Nerve` : 'Raids happen in a window'}
          </button>
        )}
        {!mine && b.ownerFamilyId !== null && war === null && (
          <Row gap={6}>
            <select
              className="btn"
              value={slot}
              onChange={(e) => setSlot(Number(e.target.value))}
              style={{ flex: 1 }}
            >
              {t.windows.map((w) => (
                <option key={w.index} value={w.slot}>
                  Strike out {w.label}
                </option>
              ))}
            </select>
            <button
              className="btn"
              disabled={busy || !inCrew}
              onClick={() => void declare(b.blockId, slot)}
            >
              Call them out
            </button>
          </Row>
        )}
        {toAnswer !== null && (
          <Col gap={5}>
            <span style={{ fontSize: 11, color: 'var(--ink-dim)' }}>
              They struck one out. Name the hour.
            </span>
            {toAnswer.answerable.map((w) => (
              <button
                key={w.index}
                className="btn"
                disabled={busy}
                onClick={() => void answer(toAnswer.warId, w.index)}
              >
                {w.label}
              </button>
            ))}
          </Col>
        )}
        {mine && (
          <button className="btn" disabled={busy} onClick={() => void cede(b.blockId)}>
            Give it up
          </button>
        )}
        <p style={{ margin: 0, fontSize: 11, color: 'var(--ink-dim)', lineHeight: 1.5 }}>
          Five of a crew at most on one block in one window. A sixth is told where there is room.
        </p>
        {refusal !== null && (
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--heat)', lineHeight: 1.5 }}>
            {refusal}
          </p>
        )}
        {refusal === null && last !== null && (
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--money)', lineHeight: 1.5 }}>{last}</p>
        )}
      </Col>
    </Panel>
  )
}

function BlockRow({ b, onPick }: { b: Block; onPick: (id: string) => void }) {
  const family = useGame().player.family
  return (
    <tr onClick={() => onPick(b.blockId)} style={{ cursor: 'pointer' }}>
      <td>
        <Row gap={7}>
          <Icon n="territory" s={13} c="var(--brass-lit)" />
          <span style={{ color: 'var(--ink-hi)' }}>{b.name}</span>
          {b.independentOrigin && (
            <span style={{ fontSize: sz(10), color: 'var(--ink-dim)' }} title="Pays 70 percent">
              70%
            </span>
          )}
        </Row>
      </td>
      <td style={{ color: 'var(--ink-mid)' }}>{TIER_LABEL[b.tier]}</td>
      <td style={{ color: 'var(--ink-mid)' }}>{moneyD(b.income / 100)}</td>
      <td style={{ color: b.control <= 25 ? 'var(--heat)' : 'var(--ink-mid)' }}>{b.control}</td>
      <td style={{ color: 'var(--ink-mid)' }}>{b.fortTierName}</td>
      <td>
        {/* The holder's NAME comes from the server, which read it from content. Never typed here. */}
        <Chip c={controlTint[b.owner]}>
          {b.ownerFamilyName ?? controlLabel(b.owner, family)}
        </Chip>
      </td>
    </tr>
  )
}

/** The row as a card, for a stacked screen: six columns do not fit a phone, and a table cut at
    the edge hides the holder, which is the column a player came to read. */
function BlockCard({ b, onPick }: { b: Block; onPick: (id: string) => void }) {
  const family = useGame().player.family
  return (
    <div
      onClick={() => onPick(b.blockId)}
      style={{ cursor: 'pointer', padding: '9px 10px', borderBottom: '1px solid var(--line-soft)' }}
    >
      <Row gap={7} style={{ flexWrap: 'wrap', rowGap: 4 }}>
        <Icon n="territory" s={13} c="var(--brass-lit)" />
        <span style={{ color: 'var(--ink-hi)', fontSize: sz(12.5), flex: '1 1 auto', minWidth: 0 }}>{b.name}</span>
        <Chip c={controlTint[b.owner]}>{b.ownerFamilyName ?? controlLabel(b.owner, family)}</Chip>
      </Row>
      <Row gap={12} style={{ marginTop: 5, flexWrap: 'wrap', rowGap: 3, fontSize: sz(11), color: 'var(--ink-mid)' }}>
        <span>{TIER_LABEL[b.tier]}</span>
        <span>{moneyD(b.income / 100)} a day</span>
        <span style={{ color: b.control <= 25 ? 'var(--heat)' : undefined }}>Control {b.control}</span>
        <span>{b.fortTierName}</span>
        {b.independentOrigin && <span title="Pays 70 percent">70%</span>}
      </Row>
    </div>
  )
}

/* ------------------------------------------------------------------ screen */

export function Territory() {
  const { selected, player } = useGame()
  const boot = useSession((s) => s.boot)
  const t = useSession((s) => s.territory)
  const load = useSession((s) => s.loadTerritory)
  useEffect(() => {
    void load().catch(() => undefined)
  }, [load])

  const all = districts()
  const district = all.find((d) => d.id === selected) ?? null
  const family = player.family
  const heroOf = (id?: string) => boot?.districts.find((d) => d.id === id)?.heroId

  const blocks = t?.blocks ?? []
  const rows = blocks.filter((b) => !selected || b.districtId === selected)
  const held = blocks.filter((b) => b.owner === 'you').length
  const [pick, setPick] = useState<string | null>(null)
  const picked = blocks.find((b) => b.blockId === pick) ?? null
  const { stacked, width } = useViewport()
  const mapH = Math.round(Math.min(Math.max(width - 16, 200) * 0.78, 460))

  return (
    <Stack>
      {/* map */}
      <Col gap={8} style={stacked ? { flex: 'none', minWidth: 0 } : { flex: 1, minWidth: 0 }}>
        <Row gap={9} style={{ flex: 'none', flexWrap: 'wrap' }}>
          <span className="t-display" style={{ fontSize: 30, color: 'var(--ink-hi)' }}>
            Territory
          </span>
          <span style={{ fontSize: 12, color: 'var(--ink-mid)', fontStyle: 'italic' }}>
            Forty blocks. Every one of them costs something to keep.
          </span>
          <div style={{ flex: 1 }} />
          {held > 0 && <Chip c={controlTint.you}>{held} held</Chip>}
        </Row>
        {stacked ? (
          <div style={{ height: mapH, flex: 'none', display: 'flex' }}>
            <CityMap />
          </div>
        ) : (
          <CityMap />
        )}

        <Panel style={stacked ? { flex: 'none' } : { height: 246, flex: 'none' }}>
          <PanelHead
            title={district ? `Blocks in ${district.name}` : 'The Forty'}
            icon="territory"
            sub={`${rows.length} of ${blocks.length}`}
            right={
              district ? (
                <Chip c={controlTint[district.control]}>
                  {controlLabel(district.control, family)}
                </Chip>
              ) : null
            }
          />
          <div className="scroll-y" style={{ flex: 1 }}>
            {rows.length > 0 && stacked && (
              <div>
                {rows.map((b) => (
                  <BlockCard key={b.blockId} b={b} onPick={setPick} />
                ))}
              </div>
            )}
            {rows.length > 0 && !stacked && (
              <table className="grid-tbl">
                <thead>
                  <tr>
                    <th>Block</th>
                    <th style={{ width: 70 }}>Tier</th>
                    <th style={{ width: 80 }}>A day</th>
                    <th style={{ width: 66 }}>Control</th>
                    <th style={{ width: 84 }}>Doors</th>
                    <th style={{ width: 150 }}>Holder</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((b) => (
                    <BlockRow key={b.blockId} b={b} onPick={setPick} />
                  ))}
                </tbody>
              </table>
            )}
            {rows.length === 0 && (
              <Empty
                line={t === null ? 'Reading the map' : 'No ground here'}
                note={t === null ? 'One moment.' : 'Pick another district.'}
              />
            )}
          </div>
        </Panel>
      </Col>

      {/* right column */}
      <Rail width={322}>
        <Panel style={{ flex: 'none', overflow: 'hidden' }}>
          <PanelHead title={district ? district.name : 'The City'} icon="pin" />
          <div
            style={{
              height: 130,
              backgroundImage: `url(${A(heroOf(district?.id) ?? 'city.master_neutral')})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        </Panel>
        <Gate />
        <BlockActions b={picked} />
        <Windows />
        <Ledger />
      </Rail>
    </Stack>
  )
}
